import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, Star, Check, X, Briefcase, FileText, Brain, Loader2, TrendingUp, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";



const ScoreBadge = ({ score, max = 10 }) => {
  const pct = (score / max) * 100;
  const color = pct >= 70 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : pct >= 40 ? "text-amber-400 border-amber-500/30 bg-amber-500/10" : "text-red-400 border-red-500/30 bg-red-500/10";
  return (
    <span className={`font-mono text-sm px-2.5 py-0.5 rounded-md border ${color}`}>
      {score}/{max}
    </span>
  );
};

export default function CandidateDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  const fetchCandidate = async () => {
    try {
      const resp = await api.get(`/candidates/${id}`);
      setCandidate(resp.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load candidate");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidate();
  }, [id]);

  const runEvaluation = async () => {
    setEvaluating(true);
    try {
      await api.post(`/candidates/${id}/evaluate`, {});
      toast.success("AI evaluation complete");
      fetchCandidate();
    } catch (e) {
      const msg = e.response?.data?.detail || "Evaluation failed";
      toast.error(msg);
    } finally {
      setEvaluating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="p-12 text-center">
        <p className="text-zinc-400">Candidate not found</p>
      </div>
    );
  }

  const parsed = candidate.parsed_data || {};
  const aiEval = candidate.ai_evaluation?.evaluation;
  const reviews = candidate.reviews || [];

  const recommendationColors = {
    strongly_recommend: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    recommend: "text-blue-400 bg-blue-500/10 border-blue-500/30",
    maybe: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    not_recommend: "text-red-400 bg-red-500/10 border-red-500/30",
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-6 lg:space-y-8 animate-fade-in" data-testid="candidate-detail-page">
      {/* Back + Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate("/candidates")}
          className="text-zinc-400 hover:text-white p-2 self-start"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl lg:text-4xl font-bold tracking-tight text-white uppercase truncate">{candidate.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <p className="text-zinc-400 text-xs sm:text-sm">{parsed.current_role || "Candidate"}{parsed.current_company ? ` at ${parsed.current_company}` : ""}</p>
            {candidate.role && (
              <Badge variant="outline" className="text-xs border-blue-500/30 text-blue-400">{candidate.role.title}</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Avg Score</p>
            <p className="font-mono text-xl lg:text-2xl font-bold text-white">{candidate.avg_score || 0}<span className="text-zinc-600 text-sm">/5</span></p>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/20">
              <Check className="w-3 h-3 text-emerald-400" />
              <span className="font-mono text-xs text-emerald-400">{candidate.accept_count}</span>
            </span>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-500/10 border border-red-500/20">
              <X className="w-3 h-3 text-red-400" />
              <span className="font-mono text-xs text-red-400">{candidate.reject_count}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Left Column - Profile */}
        <div className="lg:col-span-2 space-y-6">
          {/* Resume Info */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold text-white uppercase tracking-tight flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-400" /> Resume Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {parsed.summary && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Summary</p>
                  <p className="text-sm text-zinc-300 leading-relaxed">{parsed.summary}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {parsed.experience_years > 0 && (
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-white/5">
                    <p className="text-xs text-zinc-500">Experience</p>
                    <p className="font-mono text-lg text-white">{parsed.experience_years} years</p>
                  </div>
                )}
                {parsed.education && (
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-white/5">
                    <p className="text-xs text-zinc-500">Education</p>
                    {Array.isArray(parsed.education) ? parsed.education.map((ed, i) => (
                      <p key={i} className="text-sm text-zinc-300">{ed.degree}{ed.field ? ` in ${ed.field}` : ""}{ed.institution ? ` — ${ed.institution}` : ""}{ed.year ? ` (${ed.year})` : ""}</p>
                    )) : (
                      <p className="text-sm text-zinc-300">{parsed.education}</p>
                    )}
                  </div>
                )}
              </div>
              {/* Work History */}
              {Array.isArray(parsed.work_history) && parsed.work_history.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Work History</p>
                  <div className="space-y-3">
                    {parsed.work_history.map((wh, i) => (
                      <div key={i} className="p-3 rounded-lg bg-zinc-800/30 border border-white/5">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-zinc-200">{wh.title}</p>
                            <p className="text-xs text-zinc-400">{wh.company}</p>
                          </div>
                          {wh.duration && <span className="text-[11px] text-zinc-500 font-mono shrink-0">{wh.duration}</span>}
                        </div>
                        {wh.description && <p className="text-xs text-zinc-500 mt-1.5">{wh.description}</p>}
                        {wh.achievements?.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5">
                            {wh.achievements.map((ach, j) => (
                              <li key={j} className="text-xs text-zinc-400 flex items-start gap-1.5">
                                <TrendingUp className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" /> {ach}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {parsed.skills?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {parsed.skills.map((s, i) => (
                      <span key={i} className="px-2.5 py-1 text-xs rounded-md bg-zinc-800 text-zinc-300 border border-white/5">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {/* Certifications */}
              {Array.isArray(parsed.certifications) && parsed.certifications.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Certifications</p>
                  <div className="flex flex-wrap gap-1.5">
                    {parsed.certifications.map((c, i) => (
                      <span key={i} className="px-2.5 py-1 text-xs rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              {parsed.highlights?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Key Highlights</p>
                  <ul className="space-y-2">
                    {parsed.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                        <TrendingUp className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Parse Resume button - show if resume exists but not fully parsed */}
              {candidate.resume_url && (!Array.isArray(parsed.work_history) || parsed.work_history.length === 0) && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <FileText className="w-5 h-5 text-blue-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-zinc-300">Resume available but not fully parsed</p>
                    <p className="text-xs text-zinc-500">Click to download and parse with AI</p>
                  </div>
                  <Button
                    data-testid="parse-resume-btn"
                    onClick={async () => {
                      try {
                        toast.info("Parsing resume...");
                        await api.post(`/candidates/${id}/parse-resume`, { force: true });
                        toast.success("Resume parsed!");
                        fetchCandidate();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Parsing failed");
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm shrink-0"
                    size="sm"
                  >
                    <FileText className="w-4 h-4 mr-1.5" /> Parse Resume
                  </Button>
                </div>
              )}
              {!parsed.summary && !parsed.skills?.length && !candidate.resume_url && (
                <p className="text-sm text-zinc-500">No resume available for this candidate.</p>
              )}
            </CardContent>
          </Card>

          {/* AI Evaluation */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg font-semibold text-white uppercase tracking-tight flex items-center gap-2">
                  <Brain className="w-5 h-5 text-violet-400" /> AI Evaluation
                </CardTitle>
                <Button
                  data-testid="run-ai-eval-btn"
                  onClick={runEvaluation}
                  disabled={evaluating}
                  className="bg-violet-600 hover:bg-violet-700 text-white text-sm"
                  size="sm"
                >
                  {evaluating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {evaluating ? "Evaluating..." : aiEval ? "Re-evaluate" : "Run AI Evaluation"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {aiEval ? (
                <div className="space-y-5">
                  {/* Score & Recommendation */}
                  <div className="flex items-center gap-4">
                    <div className="text-center p-4 rounded-xl bg-zinc-800/50 border border-white/5">
                      <p className="text-xs text-zinc-500 mb-1">Score</p>
                      <p className="font-mono text-3xl font-bold text-white">{aiEval.overall_score}<span className="text-zinc-600 text-lg">/10</span></p>
                    </div>
                    <div className={`px-4 py-2 rounded-lg border ${recommendationColors[aiEval.recommendation] || "text-zinc-400 bg-zinc-800 border-white/10"}`}>
                      <p className="text-xs opacity-70 mb-0.5">Recommendation</p>
                      <p className="font-semibold text-sm uppercase tracking-wider">{aiEval.recommendation?.replace(/_/g, " ")}</p>
                    </div>
                    <div className={`px-4 py-2 rounded-lg border ${
                      aiEval.interview_decision === "yes" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" : 
                      aiEval.interview_decision === "conditional" ? "text-amber-400 bg-amber-500/10 border-amber-500/30" :
                      "text-red-400 bg-red-500/10 border-red-500/30"
                    }`}>
                      <p className="text-xs opacity-70 mb-0.5">Interview</p>
                      <p className="font-semibold text-sm uppercase">{aiEval.interview_decision}</p>
                    </div>
                  </div>

                  {/* Summary */}
                  {aiEval.summary && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Summary</p>
                      <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{aiEval.summary}</p>
                    </div>
                  )}

                  {/* Competency Scores */}
                  {aiEval.competency_scores && Object.keys(aiEval.competency_scores).length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Competency Scores</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(aiEval.competency_scores).map(([comp, score]) => (
                          <div key={comp} className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-800/50 border border-white/5">
                            <span className="text-sm text-zinc-300">{comp}</span>
                            <ScoreBadge score={score} max={10} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Strengths & Concerns */}
                  <div className="grid grid-cols-2 gap-4">
                    {aiEval.strengths?.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Strengths</p>
                        <ul className="space-y-1.5">
                          {aiEval.strengths.map((s, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-emerald-400/80">
                              <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {aiEval.concerns?.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Concerns</p>
                        <ul className="space-y-1.5">
                          {aiEval.concerns.map((c, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-red-400/80">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {aiEval.interview_notes && (
                    <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                      <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">Interview Focus Areas</p>
                      <p className="text-sm text-zinc-300">{aiEval.interview_notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Brain className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                  <p className="text-sm text-zinc-500">No AI evaluation yet. Click the button above to generate one.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Reviews */}
        <div className="space-y-6">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold text-white uppercase tracking-tight">
                Reviews ({reviews.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px] pr-3">
                {reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((r, i) => (
                      <div key={i} className="p-4 rounded-xl bg-zinc-800/50 border border-white/5 space-y-3" data-testid={`review-item-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={r.reviewer_picture} />
                              <AvatarFallback className="bg-zinc-700 text-xs">{r.reviewer_name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium text-zinc-200">{r.reviewer_name}</span>
                          </div>
                          <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold border ${
                            r.decision === "accept" ? "badge-accept" : "badge-reject"
                          }`}>
                            {r.decision.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} className={`w-3.5 h-3.5 ${s <= r.score ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
                          ))}
                          <span className="font-mono text-xs text-zinc-500 ml-1">{r.score}/5</span>
                        </div>
                        {r.comment && (
                          <p className="text-sm text-zinc-400 leading-relaxed">"{r.comment}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Star className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                    <p className="text-sm text-zinc-500">No reviews yet</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
